import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PaymentForm from '../components/PaymentForm';
import Menu from '../components/Menu';
import { TranslationContext } from "../contexts/TranslationContext";
import API_BASE_URL from "../apiConfig";
import PaypalForm from "../components/PaypalForm";
import PaymentCallback from "../components/PaymentCallback";

const Subscription = () => {
  const { translations, routes } = useContext(TranslationContext);
  const [stripePromise, setStripePromise] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(null); // null means status is still loading
  const [error, setError] = useState("");
  const [renewDate, setRenewDate] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("User is not authenticated. Please log in.");
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/settings`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json();
        if (response.ok && result.success === "true") {
          setSettings(result.data);

          const stripeMode = result.data.stripe_mode;
          const publishableKey =
            stripeMode === "test"
              ? result.data.test_published_api
              : result.data.live_published_api;
          setStripePromise(loadStripe(publishableKey));
        } else {
          setError("Failed to fetch settings.");
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        setError("An error occurred: " + error.message);
      }
    };

    fetchSettings();
  }, []);

  const fetchSubscriptionStatus = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("User is not authenticated. Please log in.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/user-subscriptions`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (response.ok) {
        if (result.success === true) {
          setIsSubscribed(true);
          setRenewDate(result.subscription.next_billing_date);
        } else {
          setIsSubscribed(false);
        }
      } else {
        setError(result.message || "Failed to fetch subscription status.");
      }
    } catch (error) {
      console.error("Error fetching subscription status:", error);
      setError("An error occurred: " + error.message);
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const handleSubscriptionSuccess = (renewDate) => {
    setIsSubscribed(true);
    setRenewDate(renewDate);
    fetchSubscriptionStatus();
  };

  if (!stripePromise || settings === null) {
    return <div className="body-loader"><i className="ri-loader-line"></i></div>;
  }
  const { enable_stripe, enable_paypal } = settings;



  return (
    <Elements stripe={stripePromise}>
      <>
        <Helmet>
          <title>{translations.subscription} - O.A.V.L.I.T.E</title>
        </Helmet>

        <div className="navbar-header container-fluid px-3 pt-2 mb-5">
          <div className="w-100"></div>
          <div className="text-center logo-wrapper pt-3">
            <Link to="/" className="logo-link">
              <img
                src="../images/oavlite-black.png"
                className="img-fluid mx-auto text-center d-block d-logo mb-3"
                alt="oavlite"
              />
            </Link>
            <p className="mb-2 fw-500 tagline__1">
              {translations.taglineV1}
            </p>

          </div>
          <Menu />
        </div>
        <div className="w-100 my-wallet-wrap text-center position-absolute mx-auto">
          <div className="container-xxl px-5">
            <div className="center-content">
              {error && <p className="text-danger text-center d-none">{error}</p>}
              <h5 className="mb-4 color-primary bgprimary p-3 premiumMention mx-auto d-none">{translations.premiumMention}</h5>
            </div>
           
            {isSubscribed === false ? (
              <p>{translations.loading}</p>
            ) : isSubscribed ? (
              <>
              <h1>{translations.subscribedTitle}</h1>
              <p>{translations.renewDate} </p>
              </>
            ) : (
              <>
                {enable_stripe === 1 && <PaymentForm onSuccess={handleSubscriptionSuccess} />}
                {enable_paypal === 1 && <PaypalForm onSuccess={handleSubscriptionSuccess} />}
                {enable_stripe === 0 && enable_paypal === 0 && (
                  <p className="text-warning">{translations.paymentMethodNotFound}</p>
                )}
                <PaymentCallback onSuccess={handleSubscriptionSuccess} />
              </>
            )}

          </div>
        </div>
        <footer className="text-center d-footer">
          <p>
            <Link className="color-primary" to={routes.legalNotices}>
              CGU
            </Link>
          </p>
        </footer>
      </>
    </Elements>
  );
};

export default Subscription;
